/* eslint-disable max-lines */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import prepareComponent from "@bluframe/grapple/prepareComponent";
import { getHomeVideoDiscsAndEditions } from "@bluframe/mightychroma-mightytool-ui";
import gql from "graphql-tag";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import List from "./List";
const DUMMY_QUERY = gql `
  query DUMMY {
    _empty
  }
`;
const DEFAULT_DEBOUNCE_DELAY = 600;
const usePrepareComponent = ({ deleteMutation, hasSearch, itemNamePlural, label, listQuery, listQueryDataName, pathPrefix, searchQuery, searchQueryDataName, searchQueryInputName, searchQueryPropertyName }) => {
    const history = useHistory();
    const [inputValue, setInputValue] = useState();
    const timeoutId = useRef(null);
    const isHomeVideoList = itemNamePlural === "home-videos";
    if (hasSearch &&
        (!searchQuery ||
            !searchQueryDataName ||
            !searchQueryInputName ||
            !searchQueryPropertyName)) {
        throw new Error("If hasSearch is true, searchQuery, searchQueryDataName, searchQueryInputName, and searchQueryPropertyName must be provided.");
    }
    const { data, loading } = useQuery(listQuery, { fetchPolicy: "network-only" });
    const [searchItems, { data: searchData, loading: searchLoading }] = useLazyQuery(searchQuery ?? DUMMY_QUERY, {
        fetchPolicy: "network-only"
    });
    const [deleteItem] = useMutation(deleteMutation, {
        refetchQueries: [listQuery]
    });
    useEffect(() => {
        if (timeoutId.current) {
            clearTimeout(timeoutId.current);
        }
        timeoutId.current = setTimeout(() => {
            if (searchQueryInputName && searchQueryPropertyName && inputValue) {
                searchItems({
                    variables: {
                        [searchQueryInputName]: {
                            [searchQueryPropertyName]: inputValue
                        }
                    }
                });
            }
        }, DEFAULT_DEBOUNCE_DELAY);
        // Clean up on unmount
        return () => {
            if (timeoutId.current) {
                clearTimeout(timeoutId.current);
            }
        };
    }, [inputValue, searchItems, searchQueryInputName, searchQueryPropertyName]);
    const onAdd = useCallback(() => {
        history.push(`${pathPrefix ?? ""}/${itemNamePlural}/add`);
    }, [pathPrefix, itemNamePlural, history]);
    const onDelete = useCallback((id) => {
        deleteItem({
            variables: {
                id
            }
        });
    }, [deleteItem]);
    const onSearchChange = useCallback((event) => {
        setInputValue(event.currentTarget.value);
    }, []);
    const onView = useCallback((id) => {
        history.push(`${pathPrefix ?? ""}/${itemNamePlural}/${id}`);
    }, [pathPrefix, itemNamePlural, history]);
    const getListItemMapper = useCallback((item) => {
        const homeVideoItem = item;
        let subtitlePrimary;
        let subtitleSecondary;
        if (isHomeVideoList) {
            const { subtitlePrimary: primary, subtitleSecondary: secondary } = getHomeVideoDiscsAndEditions(homeVideoItem);
            subtitlePrimary = primary;
            subtitleSecondary = secondary;
        }
        else {
            const editions = "editions" in homeVideoItem
                ? homeVideoItem.editions
                : undefined;
            if (editions?.length) {
                subtitleSecondary = editions.join(", ");
            }
        }
        const statusPills = isHomeVideoList
            ? [
                {
                    id: `${homeVideoItem.id}-review-pill`,
                    label: homeVideoItem.homeVideoReview?.id
                        ? "Review present"
                        : "No review",
                    tone: homeVideoItem.homeVideoReview?.id
                        ? "success"
                        : "warning"
                },
                ...(homeVideoItem.newsCount && homeVideoItem.newsCount > 0
                    ? [
                        {
                            id: `${homeVideoItem.id}-news-pill`,
                            label: `News: ${homeVideoItem.newsCount}`,
                            tone: "info"
                        }
                    ]
                    : [])
            ]
            : undefined;
        return {
            ...item,
            image: homeVideoItem?.images?.[0],
            label: item.name ??
                item.title ??
                item.url,
            onDelete,
            onView,
            statusPills,
            subtitlePrimary,
            subtitleSecondary
        };
    }, [isHomeVideoList, onDelete, onView]);
    const items = useMemo(() => {
        if (hasSearch && searchQueryDataName && searchData && inputValue) {
            return searchData[searchQueryDataName]?.map(getListItemMapper) ?? [];
        }
        if (loading || searchLoading) {
            return [];
        }
        return data?.[listQueryDataName].map(getListItemMapper) ?? [];
    }, [
        hasSearch,
        searchQueryDataName,
        searchData,
        inputValue,
        loading,
        searchLoading,
        data,
        listQueryDataName,
        getListItemMapper
    ]);
    return {
        hasSearch,
        isSearchLoading: searchLoading,
        items,
        label,
        loading,
        onAdd,
        onSearchChange
    };
};
export default prepareComponent(usePrepareComponent)(List);
